/**
 * Role and membership utilities
 * Defines role hierarchy, permissions, and membership tiers
 */

/**
 * All possible user roles in the system
 * Hierarchy: admin > owner_tier1 > owner_unpaid > manager > frontdesk > housekeeper
 */
export type UserRole = 
  | 'admin' 
  | 'owner_tier1' 
  | 'owner_unpaid' 
  | 'manager' 
  | 'frontdesk' 
  | 'housekeeper'

/**
 * Membership tier for owners
 */
export type MembershipTier = 'UNPAID' | 'TIER1' | 'TIER2' | null

/**
 * App-level features/routes that are gated by hotel membership role.
 */
export type AppFeature =
  | 'dashboard'
  | 'notifications'
  | 'admin'
  | 'studio-management'
  | 'accounts-receivable'
  | 'expenses'
  | 'dnr'
  | 'parking-permits'
  | 'projects'
  | 'bad-debt'
  | 'evictions'
  | 'inspections'
  | 'front-desk'
  | 'housekeeping'
  | 'settings'
  | 'inventory'

export const APP_FEATURE_PERMISSIONS: Record<AppFeature, UserRole[]> = {
  dashboard: ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk', 'housekeeper'],
  notifications: ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk', 'housekeeper'],
  admin: ['admin'],
  'studio-management': ['admin', 'owner_tier1', 'owner_unpaid', 'manager'],
  'accounts-receivable': ['admin', 'owner_tier1', 'owner_unpaid', 'manager'],
  expenses: ['admin', 'owner_tier1', 'owner_unpaid', 'manager'],
  dnr: ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk'],
  'parking-permits': ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk', 'housekeeper'],
  projects: ['admin', 'owner_tier1', 'owner_unpaid', 'manager'],
  'bad-debt': ['admin', 'owner_tier1', 'owner_unpaid', 'manager'],
  evictions: ['admin', 'owner_tier1', 'owner_unpaid', 'manager'],
  inspections: ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk'],
  'front-desk': ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk'],
  housekeeping: ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk', 'housekeeper'],
  settings: ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk', 'housekeeper'],
  inventory: ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk'],
}

export function canAccessFeature(
  role: string | null | undefined,
  feature: AppFeature
): boolean {
  if (!role) return false
  return APP_FEATURE_PERMISSIONS[feature].includes(role as UserRole)
}

/**
 * Features that require the hotel to be on a PAID plan — i.e. the hotel has an
 * admin or a paid owner (owner_tier1). On a free-only hotel (just an
 * owner_unpaid), these are locked for EVERY member (owner, manager, frontdesk,
 * housekeeper) until an owner upgrades. Admins and paid owners always pass
 * because their presence is exactly what makes the hotel "paid".
 */
export const PREMIUM_FEATURES: AppFeature[] = ['dnr', 'studio-management', 'inventory']

export function isPremiumFeature(feature: AppFeature): boolean {
  return PREMIUM_FEATURES.includes(feature)
}

/**
 * Combined access gate: the role allows the feature AND, when the feature is
 * premium, the hotel is on a paid plan. `hotelHasPremium` should reflect the
 * currently selected hotel (see hotel_has_premium_access RPC).
 */
export function canUseFeature(
  role: string | null | undefined,
  feature: AppFeature,
  hotelHasPremium: boolean
): boolean {
  if (!canAccessFeature(role, feature)) return false
  if (isPremiumFeature(feature) && !hotelHasPremium) return false
  return true
}

export function getRoleFeatures(role: string | null | undefined): AppFeature[] {
  return (Object.keys(APP_FEATURE_PERMISSIONS) as AppFeature[]).filter((feature) =>
    canAccessFeature(role, feature)
  )
}

/**
 * Role hierarchy levels (higher number = more permissions)
 */
const ROLE_HIERARCHY: Record<UserRole, number> = {
  admin: 100,
  owner_tier1: 80,
  owner_unpaid: 70,
  manager: 50,
  frontdesk: 30,
  housekeeper: 10,
}

/**
 * Check if a role is an owner role
 */
export function isOwnerRole(role: string | null | undefined): boolean {
  return role === 'owner_unpaid' || role === 'owner_tier1'
}

/**
 * Check if a role is an admin role
 */
export function isAdminRole(role: string | null | undefined): boolean {
  return role === 'admin'
}

/**
 * Check if a role can access the Hotel Admin Center
 */
export function canAccessHotelAdminCenter(role: string | null | undefined): boolean {
  return role === 'admin' || isOwnerRole(role)
}

/**
 * Get membership tier from role
 */
export function getMembershipTier(role: string | null | undefined): MembershipTier {
  if (role === 'owner_unpaid') return 'UNPAID'
  if (role === 'owner_tier1') return 'TIER1'
  if (role === 'owner_tier2') return 'TIER2'
  return null
}

/**
 * Get invite limit for a role
 */
export function getInviteLimit(role: string | null | undefined): number {
  switch (role) {
    case 'admin':
      return 9999 // Unlimited
    case 'owner_tier1':
      return 15
    case 'owner_unpaid':
      return 1
    default:
      return 0
  }
}

/**
 * Check if a role can invite users
 */
export function canInviteUsers(role: string | null | undefined): boolean {
  return role === 'admin' || isOwnerRole(role)
}

/**
 * Check if a role can assign another role.
 * - Admins can assign any role.
 * - Paid owners (owner_tier1) can invite co-owners, but only as a free trial
 *   (owner_unpaid) — not another paid owner (only admins grant the paid tier).
 * - Free owners (owner_unpaid) cannot invite any owner; they must upgrade to
 *   Tier 1 first. They can still assign staff roles.
 */
export function canAssignRole(
  assignerRole: string | null | undefined,
  targetRole: UserRole
): boolean {
  if (!assignerRole) return false

  // Admins can assign any role
  if (assignerRole === 'admin') return true

  if (isOwnerRole(assignerRole)) {
    if (isAdminRole(targetRole)) return false
    // Only admins can grant the paid owner tier.
    if (targetRole === 'owner_tier1') return false
    // Only a paid owner can invite a (trial) co-owner.
    if (targetRole === 'owner_unpaid') return assignerRole === 'owner_tier1'
    return true // manager / frontdesk / housekeeper
  }

  // Other roles cannot assign roles
  return false
}

/**
 * Get role display name
 */
export function getRoleDisplayName(role: string | null | undefined): string {
  switch (role) {
    case 'admin':
      return 'Admin'
    case 'owner_tier1':
      return 'Owner (Tier 1)'
    case 'owner_unpaid':
      return 'Owner (Free)'
    case 'manager':
      return 'Manager'
    case 'frontdesk':
      return 'Front Desk'
    case 'housekeeper':
      return 'Housekeeper'
    default:
      return role || 'Unknown'
  }
}

/**
 * Get role badge color
 */
export function getRoleBadgeColor(role: string | null | undefined): string {
  switch (role) {
    case 'admin':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    case 'owner_tier1':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    case 'owner_unpaid':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    case 'manager':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'frontdesk':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    case 'housekeeper':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }
}

/**
 * Compare two roles - returns true if role1 has higher or equal permissions than role2
 */
export function hasRoleOrHigher(
  userRole: string | null | undefined,
  requiredRole: UserRole
): boolean {
  if (!userRole) return false
  const userLevel = ROLE_HIERARCHY[userRole as UserRole] || 0
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0
  return userLevel >= requiredLevel
}

/**
 * Get available roles that a user can assign
 */
export function getAssignableRoles(assignerRole: string | null | undefined): UserRole[] {
  if (!assignerRole) return []
  
  if (assignerRole === 'admin') {
    // Admins can assign any role
    return ['admin', 'owner_tier1', 'owner_unpaid', 'manager', 'frontdesk', 'housekeeper']
  }
  
  if (isOwnerRole(assignerRole)) {
    const staff: UserRole[] = ['manager', 'frontdesk', 'housekeeper']
    // Only paid (Tier 1) owners can invite a co-owner — always as a free-trial
    // owner_unpaid. Free owners must upgrade before they can invite owners.
    return assignerRole === 'owner_tier1' ? ['owner_unpaid', ...staff] : staff
  }

  // Other roles cannot assign roles
  return []
}
